import { createContext, useState, useCallback, useContext } from 'react';
import { useSelector } from 'react-redux';
import { isAIEnabled } from 'shared/store/selectors/features.selector';
import { getLang } from 'shared/store/selectors/lang.selector';
import zeusServiceApi from 'shared/api/zeus.service.api';

const ChatContext = createContext();

export function useChatConversation() {
  return useContext(ChatContext);
}

export function ChatProvider({ children }) {
  const [conversationId, setConversationId] = useState(undefined);
  const [loading, setLoadingState] = useState(false);
  const [messages, setMessages] = useState([]);

  const lang = useSelector(getLang('AI_CHAT'));

  const isOpen = useSelector(state => state.ai.isChatOpen);

  const isChatEnabled = useSelector(isAIEnabled);

  const onMessageSend = useCallback(
    async values => {
      setLoadingState(true);

      setMessages(prevMessages => [
        ...prevMessages,
        {
          id: Math.random(),
          message: values.message,
          isUser: true,
        },
      ]);

      const res = await zeusServiceApi.chatMessage({ term: values.message, conversationId });

      if (res.success) {
        const { answer, references, conversationId, messageId } = res.data;

        setMessages(prevMessages => [
          ...prevMessages,
          {
            id: messageId,
            message: answer,
            references,
            isUser: false,
            error: false,
          },
        ]);

        setConversationId(conversationId);
      } else {
        setMessages(prevMessages => [
          ...prevMessages,
          {
            id: Math.random(),
            message: lang.ERROR_MESSAGE,
            isUser: false,
            error: true,
          },
        ]);
      }

      setLoadingState(false);
    },
    [conversationId, lang.ERROR_MESSAGE],
  );

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
  }, []);

  const value = {
    conversationId,
    loading,
    messages,
    lang,
    isOpen,
    isChatEnabled,
    onMessageSend,
    clearChat,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}
